import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { Avatar } from "./avatar";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "./dropdown";
import { Navbar, NavbarItem, NavbarSection, NavbarSpacer } from "./navbar";
import {
  Sidebar,
  SidebarBody,
  SidebarDivider,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from "./sidebar";
import { SidebarLayout } from "./sidebar-layout";
import {
  ArrowRightStartOnRectangleIcon,
  ChevronUpIcon,
  Cog8ToothIcon,
  ShieldCheckIcon,
  LightBulbIcon,
  UserIcon,
  UserGroupIcon,
  HomeIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  ChartBarIcon,
  BanknotesIcon,
  ClockIcon,
  BuildingOffice2Icon,
  BriefcaseIcon,
  ChevronUpDownIcon,
  InboxIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/20/solid";
import HeartbeatIcon from "./icons/HeartbeatIcon";
import { usePortalView } from "../contexts/PortalViewContext";
import { useBandejaEmails } from "../hooks/useBandejaEmails";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { Heading } from "./heading";
import { Dialog, DialogTitle, DialogBody, DialogActions } from "./dialog";
import { Button } from "./button";
import { useUser } from "./UserContext";
import { useBranding } from "../hooks/useBranding";
import PendientesBurbuja from "./PendientesBurbuja";
import CampanaNotificaciones from "./CampanaNotificaciones";
import { ClipboardDocumentCheckIcon } from "@heroicons/react/20/solid";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const { userData, loading, isOwner, isClient, isOperator, hasConvenioAccess, orgData } = useUser();
  const branding = useBranding();
  // Unread dot for the portal's Bandeja: any email about the user's clientes
  // newer than their last visit stamp. userData is live (onSnapshot), so
  // opening the Bandeja clears the dot without a reload.
  const bandejaEmails = useBandejaEmails();
  const bandejaHasNew = useMemo(() => {
    if (!isClient || !bandejaEmails) return false;
    const lastSeen = userData?.bandejaVistaAt || 0;
    return bandejaEmails.some((row) => (row.sentAt?.toMillis?.() || 0) > lastSeen);
  }, [isClient, userData?.bandejaVistaAt, bandejaEmails]);
  const orgHasConvenios = Boolean(orgData?.hasConvenios);
  // Órdenes de Trabajo: an add-on Surelia switches on per org.
  const orgHasOt = Boolean(orgData?.otEnabled);
  const hasClientAccess = Boolean(userData?.clientIds?.length > 0);
  // The portal's workspace switcher: one context per fiado (empresa) and per
  // convenio. The nav below renders for the ACTIVE context only — never both
  // worlds stacked.
  const { contexts: portalContexts, activeContext, activeClienteId, activeConvenioId, setActiveContextKey } = usePortalView();
  const portalViewType = activeContext?.type || (hasClientAccess ? 'cliente' : hasConvenioAccess ? 'convenio' : null);

  const handleContextSwitch = (key) => {
    if (key === activeContext?.key) return;
    setActiveContextKey(key);
    navigate('/app/portal');
  };

  // Redirect client users away from broker routes
  useEffect(() => {
    if (isClient && !location.pathname.startsWith('/app/portal')) {
      navigate('/app/portal', { replace: true });
    }
  }, [isClient, location.pathname, navigate]);

  // Show branded loading screen while user data loads
  if (loading || !userData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-azul-surelia">
        {/* Only show a logo when we actually know the org's brand (live or
            cached). Never fall back to the Surelia logo here — a neutral
            spinner avoids flashing the wrong brand. */}
        {branding.isWhiteLabel && (
          <div className="mb-8">
            <img
              src={branding.logoUrl}
              alt={branding.nombreComercial}
              className="h-12"
            />
          </div>
        )}
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-white"></div>
        <p className="mt-4 text-white/70 text-sm">Cargando...</p>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <div className="flex h-full bg-green-500">
      <SidebarLayout
        navbar={
          <Navbar>
            <NavbarSpacer />
            <NavbarSection>
              <Dropdown>
                <DropdownButton as={NavbarItem}>
                  <Avatar
                    slot="icon"
                    initials={userData?.nombre?.charAt(0) || '?'}
                    className="size-8 bg-verde-lima text-azul-surelia"
                    square
                  />
                </DropdownButton>
                <DropdownMenu className="min-w-64" anchor="bottom end">
                  {!isClient && (
                    <>
                      <DropdownItem href="/app/config?tab=perfil">
                        <UserIcon />
                        <DropdownLabel>Perfil</DropdownLabel>
                      </DropdownItem>
                      <DropdownItem href="/app/config?tab=organizacion">
                        <Cog8ToothIcon />
                        <DropdownLabel>Configuración</DropdownLabel>
                      </DropdownItem>
                      <DropdownDivider />
                    </>
                  )}
                  <DropdownItem as="button" onClick={() => setIsPrivacyOpen(true)}>
                    <ShieldCheckIcon />
                    <DropdownLabel>Política de Privacidad</DropdownLabel>
                  </DropdownItem>
                  {!isClient && (
                    <>
                      <DropdownItem href="/app/support">
                        <LightBulbIcon />
                        <DropdownLabel>Feedback</DropdownLabel>
                      </DropdownItem>
                    </>
                  )}
                  <DropdownDivider />
                  <DropdownItem as="button" onClick={handleLogout}>
                    <ArrowRightStartOnRectangleIcon />
                    <DropdownLabel>Cerrar Sesión</DropdownLabel>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </NavbarSection>
          </Navbar>
        }
        sidebar={
          <Sidebar
            isCollapsed={isCollapsed}
            onCollapseChange={() => setIsCollapsed(!isCollapsed)}
          >
            <SidebarHeader
              isCollapsed={isCollapsed}
              onCollapseChange={() => setIsCollapsed(!isCollapsed)}
              light={branding.isWhiteLabel && branding.logoBgWhite}
            >
              <Heading className={isCollapsed ? "hidden" : ""}>
                <a href="/">
                  <img
                    src={branding.logoUrl}
                    alt={branding.nombreComercial}
                  />
                </a>
              </Heading>
            </SidebarHeader>
            <SidebarBody isCollapsed={isCollapsed}>
              {isClient ? (
                <>
                  <SidebarSection isCollapsed={isCollapsed}>
                  {portalContexts.length > 0 && (() => {
                    const switchable = portalContexts.length > 1;
                    const badgeInner = (
                      <>
                        {activeContext?.type === 'convenio' ? (
                          <BuildingOffice2Icon className="w-4 h-4 shrink-0 text-verde-lima" />
                        ) : (
                          <BriefcaseIcon className="w-4 h-4 shrink-0 text-verde-lima" />
                        )}
                        {!isCollapsed && (
                          <>
                            <span className="flex-1 min-w-0">
                              <span className="block text-[10px] uppercase tracking-wide text-white/50">
                                {activeContext?.type === 'convenio' ? 'Convenio' : 'Empresa'}
                              </span>
                              <span className="block text-sm font-medium truncate">
                                {activeContext?.label}
                              </span>
                            </span>
                            {switchable && <ChevronUpDownIcon className="w-4 h-4 shrink-0 text-white/60" />}
                          </>
                        )}
                      </>
                    );
                    const badgeClass = `w-full flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 text-left text-white transition-colors ${
                      switchable ? 'hover:bg-white/15' : ''
                    } ${isCollapsed ? 'justify-center px-2 py-2' : 'px-2.5 py-2'}`;
                    return (
                      <>
                        {switchable ? (
                          <Dropdown>
                            <DropdownButton as="button" className={badgeClass} title={activeContext?.label}>
                              {badgeInner}
                            </DropdownButton>
                            <DropdownMenu className="min-w-64" anchor="bottom start">
                              {portalContexts.map((ctx, i) => {
                                const prev = portalContexts[i - 1];
                                const groupChanged = prev && prev.type !== ctx.type;
                                return (
                                  <React.Fragment key={ctx.key}>
                                    {groupChanged && <DropdownDivider />}
                                    <DropdownItem as="button" onClick={() => handleContextSwitch(ctx.key)}>
                                      {ctx.type === 'convenio' ? <BuildingOffice2Icon /> : <BriefcaseIcon />}
                                      <DropdownLabel className={ctx.key === activeContext?.key ? 'font-semibold' : ''}>
                                        {ctx.label}
                                      </DropdownLabel>
                                    </DropdownItem>
                                  </React.Fragment>
                                );
                              })}
                            </DropdownMenu>
                          </Dropdown>
                        ) : (
                          <div className={badgeClass} title={activeContext?.label}>
                            {badgeInner}
                          </div>
                        )}
                        {/* mt-3.5 + the section's gap-0.5 = 16px under the badge,
                            matching the body's p-4 above it. */}
                        <SidebarDivider isCollapsed={isCollapsed} className="!mt-3.5 !mb-2 !border-white/20" />
                      </>
                    );
                  })()}
                    <SidebarItem
                      onClick={() => navigate("/app/portal")}
                      current={location.pathname === "/app/portal"}
                      isCollapsed={isCollapsed}
                    >
                      <HomeIcon />
                      <SidebarLabel isCollapsed={isCollapsed}>Inicio</SidebarLabel>
                    </SidebarItem>
                    {/* Bandeja rows are clientId-scoped, so convenio-only
                        users would only ever see an empty inbox — hide it
                        for them. */}
                    {hasClientAccess && (
                      <SidebarItem
                        onClick={() => navigate("/app/portal/bandeja")}
                        current={location.pathname === "/app/portal/bandeja"}
                        isCollapsed={isCollapsed}
                      >
                        <InboxIcon />
                        <SidebarLabel isCollapsed={isCollapsed}>Bandeja</SidebarLabel>
                        {bandejaHasNew && !isCollapsed && (
                          <span className="ml-1.5 w-2 h-2 rounded-full bg-verde-lima flex-shrink-0" />
                        )}
                      </SidebarItem>
                    )}
                    {/* The fiado context mirrors the convenio one: a single
                        Empresa entry into the tabbed page (Resumen/Contratos/
                        Fianzas/Expediente/Afianzadoras). The old standalone
                        pages redirect into its tabs. */}
                    {portalViewType === 'cliente' && (
                      <SidebarItem
                        onClick={() => navigate(activeClienteId ? `/app/portal/cliente/${activeClienteId}` : "/app/portal/expedientes")}
                        current={
                          location.pathname.startsWith("/app/portal/cliente/") ||
                          ["/app/portal/expedientes", "/app/portal/contratos", "/app/portal/fianzas", "/app/portal/afianzadoras"].includes(location.pathname)
                        }
                        isCollapsed={isCollapsed}
                      >
                        <BriefcaseIcon />
                        <SidebarLabel isCollapsed={isCollapsed}>Empresa</SidebarLabel>
                      </SidebarItem>
                    )}
                    {portalViewType === 'convenio' && (
                      <SidebarItem
                        onClick={() => navigate(activeConvenioId ? `/app/portal/convenios/${activeConvenioId}` : "/app/portal/convenios")}
                        current={location.pathname.startsWith("/app/portal/convenios")}
                        isCollapsed={isCollapsed}
                      >
                        <BuildingOffice2Icon />
                        <SidebarLabel isCollapsed={isCollapsed}>Convenio</SidebarLabel>
                      </SidebarItem>
                    )}
                    {portalViewType && (
                      <SidebarItem
                        onClick={() => navigate("/app/portal/reportes")}
                        current={location.pathname === "/app/portal/reportes"}
                        isCollapsed={isCollapsed}
                      >
                        <ChartBarIcon />
                        <SidebarLabel isCollapsed={isCollapsed}>Reportes</SidebarLabel>
                      </SidebarItem>
                    )}
                  </SidebarSection>
                  <SidebarSpacer isCollapsed={isCollapsed} />
                  <SidebarSection isCollapsed={isCollapsed}>
                    <SidebarItem
                      as="button"
                      onClick={handleLogout}
                      isCollapsed={isCollapsed}
                    >
                      <ArrowRightStartOnRectangleIcon />
                      <SidebarLabel isCollapsed={isCollapsed}>
                        Cerrar Sesión
                      </SidebarLabel>
                    </SidebarItem>
                  </SidebarSection>
                </>
              ) : (
                <>
              <SidebarSection isCollapsed={isCollapsed}>
                <SidebarItem
                  onClick={() => navigate("/app/inicio")}
                  current={location.pathname === "/app/inicio"}
                  isCollapsed={isCollapsed}
                >
                  <HomeIcon />
                  <SidebarLabel isCollapsed={isCollapsed}>Inicio</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                  onClick={() => navigate("/app/pulso")}
                  current={location.pathname === "/app/pulso"}
                  isCollapsed={isCollapsed}
                >
                  <HeartbeatIcon />
                  <SidebarLabel isCollapsed={isCollapsed}>Pulso</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                  onClick={() => navigate("/app/pendientes")}
                  current={location.pathname === "/app/pendientes"}
                  isCollapsed={isCollapsed}
                >
                  <ClipboardDocumentListIcon />
                  <SidebarLabel isCollapsed={isCollapsed}>
                    Pendientes
                  </SidebarLabel>
                </SidebarItem>
                {orgHasOt && (
                  <SidebarItem
                    onClick={() => navigate("/app/ordenes")}
                    current={location.pathname === "/app/ordenes"}
                    isCollapsed={isCollapsed}
                  >
                    <ClipboardDocumentCheckIcon />
                    <SidebarLabel isCollapsed={isCollapsed}>
                      Órdenes de trabajo
                    </SidebarLabel>
                  </SidebarItem>
                )}
                <SidebarItem
                  onClick={() => navigate("/app/clientes")}
                  current={location.pathname.startsWith("/app/clientes")}
                  isCollapsed={isCollapsed}
                >
                  <UserGroupIcon />
                  <SidebarLabel isCollapsed={isCollapsed}>
                    Clientes
                  </SidebarLabel>
                </SidebarItem>
                <SidebarItem
                  onClick={() => navigate("/app/contratos")}
                  current={location.pathname === "/app/contratos"}
                  isCollapsed={isCollapsed}
                >
                  <DocumentTextIcon />
                  <SidebarLabel isCollapsed={isCollapsed}>
                    Contratos
                  </SidebarLabel>
                </SidebarItem>
                <SidebarItem
                  onClick={() => navigate("/app/fianzas")}
                  current={location.pathname === "/app/fianzas"}
                  isCollapsed={isCollapsed}
                >
                  <DocumentCheckIcon />
                  <SidebarLabel isCollapsed={isCollapsed}>
                    Fianzas
                  </SidebarLabel>
                </SidebarItem>
                {orgHasConvenios && (
                  <SidebarItem
                    onClick={() => navigate("/app/convenios")}
                    current={location.pathname.startsWith("/app/convenios")}
                    isCollapsed={isCollapsed}
                  >
                    <BuildingOffice2Icon />
                    <SidebarLabel isCollapsed={isCollapsed}>
                      Convenios
                    </SidebarLabel>
                  </SidebarItem>
                )}
                {/* Reportes - el owner ve todos; el operator solo los reportes
                    operativos (cobranza, saldos, cartera). */}
                {(isOwner || isOperator) && (
                  <SidebarItem
                    onClick={() => navigate("/app/reportes")}
                    current={location.pathname === "/app/reportes"}
                    isCollapsed={isCollapsed}
                  >
                    <ChartBarIcon />
                    <SidebarLabel isCollapsed={isCollapsed}>
                      Reportes
                    </SidebarLabel>
                  </SidebarItem>
                )}
                {/* Comisiones - owners only (the comisión ledger) */}
                {isOwner && (
                  <SidebarItem
                    onClick={() => navigate("/app/comisiones")}
                    current={location.pathname === "/app/comisiones"}
                    isCollapsed={isCollapsed}
                  >
                    <BanknotesIcon />
                    <SidebarLabel isCollapsed={isCollapsed}>
                      Comisiones
                    </SidebarLabel>
                  </SidebarItem>
                )}
                <SidebarItem
                  onClick={() => navigate("/app/bitacora")}
                  current={location.pathname === "/app/bitacora"}
                  isCollapsed={isCollapsed}
                >
                  <ClockIcon />
                  <SidebarLabel isCollapsed={isCollapsed}>
                    Bitácora
                  </SidebarLabel>
                </SidebarItem>
                <SidebarItem
                  onClick={() => navigate("/app/config")}
                  current={location.pathname === "/app/config"}
                  isCollapsed={isCollapsed}
                >
                  <Cog8ToothIcon />
                  <SidebarLabel isCollapsed={isCollapsed}>
                    Configuración
                  </SidebarLabel>
                </SidebarItem>
              </SidebarSection>
              <SidebarSpacer isCollapsed={isCollapsed} />
              <SidebarSection isCollapsed={isCollapsed}>
                {/* La campana: avisos de las órdenes de trabajo (solo orgs con OTs). */}
                <CampanaNotificaciones activo={orgHasOt} compacto={isCollapsed} />
                <SidebarItem
                  onClick={() => navigate("/app/support")}
                  current={location.pathname === "/app/support"}
                  isCollapsed={isCollapsed}
                >
                  <QuestionMarkCircleIcon />
                  <SidebarLabel isCollapsed={isCollapsed}>
                    Soporte Técnico
                  </SidebarLabel>
                </SidebarItem>
                <SidebarItem
                  onClick={() => navigate("/app/changelog")}
                  current={location.pathname === "/app/changelog"}
                  isCollapsed={isCollapsed}
                >
                  <SparklesIcon />
                  <SidebarLabel isCollapsed={isCollapsed}>
                    Avisos de la plataforma
                  </SidebarLabel>
                </SidebarItem>
              </SidebarSection>
                </>
              )}
            </SidebarBody>

            <SidebarFooter className="max-lg:hidden" isCollapsed={isCollapsed}>
              <Dropdown>
                <DropdownButton as={SidebarItem} isCollapsed={isCollapsed}>
                  <span className="flex min-w-0 items-center gap-3">
                    <Avatar
                      slot="icon"
                      initials={userData?.nombre?.charAt(0)}
                      className="size-10 bg-verde-lima text-azul-surelia"
                      square
                    />
                    {!isCollapsed && (
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-white">
                          {userData?.nombre}
                        </span>
                        <span className="block truncate text-xs font-normal text-white/70">
                          {userData?.correo}
                        </span>
                      </span>
                    )}
                  </span>
                  {!isCollapsed && <ChevronUpIcon />}
                </DropdownButton>
                <DropdownMenu className="min-w-64" anchor="top start">
                  {!isClient && (
                    <>
                      <DropdownItem href="/app/config?tab=perfil">
                        <UserIcon />
                        <DropdownLabel>Perfil</DropdownLabel>
                      </DropdownItem>
                      <DropdownItem href="/app/config?tab=organizacion">
                        <Cog8ToothIcon />
                        <DropdownLabel>Configuración</DropdownLabel>
                      </DropdownItem>
                      <DropdownDivider />
                    </>
                  )}
                  <DropdownItem as="button" onClick={() => setIsPrivacyOpen(true)}>
                    <ShieldCheckIcon />
                    <DropdownLabel>Política de Privacidad</DropdownLabel>
                  </DropdownItem>
                  {!isClient && (
                    <>
                      <DropdownItem href="/app/support">
                        <LightBulbIcon />
                        <DropdownLabel>Feedback</DropdownLabel>
                      </DropdownItem>
                    </>
                  )}
                  <DropdownDivider />
                  <DropdownItem as="button" onClick={handleLogout}>
                    <ArrowRightStartOnRectangleIcon />
                    <DropdownLabel>Cerrar Sesión</DropdownLabel>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </SidebarFooter>
          </Sidebar>
        }
        isCollapsed={isCollapsed}
        onCollapseChange={() => setIsCollapsed(!isCollapsed)}
        wide={location.pathname.startsWith("/app/reportes") || location.pathname.startsWith("/app/ordenes")}
      >
        <Outlet />
      </SidebarLayout>

      {/* La bolita: pendientes siempre a la mano. Broker-only; hides itself
          on portal routes. */}
      {!isClient && <PendientesBurbuja />}

      {/* Privacy Policy Modal */}
      <Dialog open={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} size="md">
        <DialogTitle>Política de Privacidad</DialogTitle>
        <DialogBody className="space-y-4 text-sm text-zinc-700 leading-relaxed max-h-[60vh] overflow-y-auto">
          <p className="text-xs text-zinc-400">Última actualización: 6 de febrero de 2026</p>

          <p>
            <strong>Kaution Consulting S.A. de C.V.</strong>, con domicilio en Ciudad de México, México, es responsable del tratamiento de sus datos personales.
          </p>
          <p>
            <strong>Surelia</strong> es un nombre de marca en proceso de registro por Kaution Consulting S.A. de C.V.
          </p>

          <h4 className="font-semibold text-azul-surelia pt-2">Datos que recopilamos</h4>
          <p>
            Recopilamos los datos personales que usted nos proporciona directamente al utilizar nuestra plataforma, incluyendo: nombre, correo electrónico, información de su organización y documentos que usted cargue voluntariamente para su procesamiento.
          </p>

          <h4 className="font-semibold text-azul-surelia pt-2">Finalidad del tratamiento</h4>
          <p>
            Sus datos personales serán utilizados para: (i) proveer los servicios contratados de gestión de fianzas; (ii) administrar su cuenta de usuario; (iii) procesar y analizar documentos mediante herramientas de inteligencia artificial; (iv) enviar comunicaciones relacionadas con el servicio.
          </p>

          <h4 className="font-semibold text-azul-surelia pt-2">Protección de datos</h4>
          <p>
            Implementamos medidas de seguridad técnicas, administrativas y físicas para proteger sus datos personales contra daño, pérdida, alteración, acceso o tratamiento no autorizado, de conformidad con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP).
          </p>

          <h4 className="font-semibold text-azul-surelia pt-2">Uso de cookies y tecnologías de rastreo</h4>
          <p>
            Utilizamos cookies y tecnologías similares para mantener su sesión activa y mejorar la experiencia de uso de la plataforma. No utilizamos cookies con fines publicitarios.
          </p>

          <h4 className="font-semibold text-azul-surelia pt-2">Conservación de datos</h4>
          <p>
            Sus datos personales serán conservados mientras mantenga una cuenta activa en la plataforma. Una vez cancelada su cuenta, sus datos serán eliminados en un plazo no mayor a 90 días, salvo que exista una obligación legal que requiera su conservación.
          </p>

          <h4 className="font-semibold text-azul-surelia pt-2">Derechos ARCO</h4>
          <p>
            Usted tiene derecho a Acceder, Rectificar, Cancelar u Oponerse al tratamiento de sus datos personales (derechos ARCO). Para ejercer estos derechos, envíe su solicitud a: <a href="mailto:arco@surelia.mx" className="text-azul-surelia underline">arco@surelia.mx</a>. Daremos respuesta en un plazo máximo de 20 días hábiles.
          </p>
          <p>
            Si considera que sus derechos no han sido atendidos adecuadamente, puede presentar una queja ante el INAI o la autoridad competente que en su momento lo sustituya en materia de protección de datos personales.
          </p>

          <h4 className="font-semibold text-azul-surelia pt-2">Transferencia de datos</h4>
          <p>
            No compartimos sus datos personales con terceros, salvo cuando sea necesario para la prestación del servicio o cuando exista una obligación legal. Para la operación de la plataforma, utilizamos los siguientes proveedores de servicios tecnológicos: <a href="https://cloud.google.com/" target="_blank" rel="noopener noreferrer" className="text-azul-surelia underline">Google Cloud Services</a>, <a href="https://www.saptiva.com/" target="_blank" rel="noopener noreferrer" className="text-azul-surelia underline">Saptiva</a>, <a href="https://vercel.com/" target="_blank" rel="noopener noreferrer" className="text-azul-surelia underline">Vercel</a> y <a href="https://stripe.com/" target="_blank" rel="noopener noreferrer" className="text-azul-surelia underline">Stripe</a>.
          </p>

          <h4 className="font-semibold text-azul-surelia pt-2">Edad mínima</h4>
          <p>
            El uso de esta plataforma está dirigido a personas mayores de 18 años. No recopilamos intencionalmente datos de menores de edad.
          </p>

          <h4 className="font-semibold text-azul-surelia pt-2">Cambios al aviso de privacidad</h4>
          <p>
            Nos reservamos el derecho de modificar este aviso de privacidad. Cualquier cambio será notificado a través de la plataforma.
          </p>

          <h4 className="font-semibold text-azul-surelia pt-2">Consentimiento</h4>
          <p>
            Al utilizar la plataforma Surelia, usted manifiesta su consentimiento para el tratamiento de sus datos personales conforme a lo establecido en el presente aviso de privacidad.
          </p>

          <h4 className="font-semibold text-azul-surelia pt-2">Legislación aplicable y jurisdicción</h4>
          <p>
            Este aviso de privacidad se rige por la legislación mexicana aplicable, incluyendo la LFPDPPP y su Reglamento. Para cualquier controversia, las partes se someten a la jurisdicción de los tribunales competentes de la Ciudad de México.
          </p>
        </DialogBody>
        <DialogActions>
          <Button color="surelia" onClick={() => setIsPrivacyOpen(false)}>
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Dashboard;
